// Unpublished Work © 2020 Deere & Company.

import {ohDeere} from '@deere/oh-deere-boundary';
import {get as getTranslations} from '@deere/translation-service';
import {noop} from '@tanstack/react-query';
import fetchIntercept from 'fetch-intercept';
import {sessionQueryOptions} from './App/hooks/useSession';
import {selectedOrganizationsQueryOptions} from './App/queries/selectedOrganizationsQueryOptions';
import {logUiErrors} from './App/utils/log-ui-errors';
import {get} from './common/utils/config';
import {setLocaleOptions} from './common/utils/locale';
import {
    redirectToLoginPage,
    shouldRedirectToLogin,
} from './common/utils/sessions';
import {
    CROPS_STEP,
    DATA_LOAD,
    FIELDS_STEP,
    WORK_PLANS_STEP,
} from './DataDog/constants/datadogActionConstants';
import {
    startActionTracking,
    startAppLoadTracking,
} from './DataDog/services/datadogActionService';
import {initRum} from './rumService';
import {queryClient} from './store/queryClient';
import './common/utils/polyfills';

const SERVICE_NAME = 'work-planner-client';
const isPersistentHeaderLoginRedirect =
    window.location.pathname === '/login' &&
    new URLSearchParams(window.location.search).has('next');

window.onerror = logUiErrors;

fetchIntercept.register({
    request(url, conf) {
        let newUrl = url,
            newConf = conf;

        if (!/^(http|https):\/\//.test(url)) {
            newUrl = `${get<string>('REACT_APP_SERVER_CORE_URL')}${url}`;
            newConf = {
                ...conf,
                credentials: conf?.credentials || 'include',
            };
        }

        return [newUrl, newConf];
    },
});

(async () => {
    const env = get<string>('NODE_ENV');

    try {
        initRum(env);
        setLocaleOptions();

        queryClient.prefetchQuery({
            queryKey: ['Translations'],
            queryFn: () => getTranslations('work-planner'),
            staleTime: Infinity,
        });

        await queryClient.fetchQuery(sessionQueryOptions);

        if (isPersistentHeaderLoginRedirect) {
            redirectToLoginPage('/');

            return;
        }
        queryClient.query(selectedOrganizationsQueryOptions).catch(noop);

        const appLoader = await import('./AppLoader');

        appLoader.loadApp();

        startAppLoadTracking();
        startActionTracking(DATA_LOAD, [
            WORK_PLANS_STEP,
            FIELDS_STEP,
            CROPS_STEP,
        ]);
    } catch (error) {
        if (shouldRedirectToLogin(error)) {
            redirectToLoginPage(
                isPersistentHeaderLoginRedirect ? '/' : undefined,
            );

            return;
        }

        const startupError =
            error instanceof Error
                ? error
                : new Error('Application startup failed.', {cause: error});

        ohDeere(
            startupError,
            SERVICE_NAME,
            `git:${process.env.GITHUB_SHA}`,
            env,
        );
    }
})();
